import { SubmitHandler, useForm } from '@agrotoken-tech/ui';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useMutation } from '@tanstack/react-query';
import { upsertProductiveAffidavit } from '../../../../../services/productive-facilities/upsertProductiveAffidavit';
import { CommonUpsertProductiveAffidavitDto } from '../../../../../services/types/query-params';
import { CommonProductiveAffidavitSerializer } from '@bcba/serializers';
import { useEffect } from 'react';

const resolver = classValidatorResolver(CommonUpsertProductiveAffidavitDto);

interface Props {
  onSuccess: () => void;
  facilityUuid: string;
  selectedAffidavit?: CommonProductiveAffidavitSerializer;
}

export const useUpsertProductiveAffidavit = ({
  onSuccess,
  facilityUuid,
  selectedAffidavit,
}: Props) => {
  const { mutate, isPending: isPendingUpsertAffidavit } = useMutation({
    mutationFn: (data: CommonUpsertProductiveAffidavitDto) =>
      upsertProductiveAffidavit(data),
    onSuccess,
  });

  const onSubmit: SubmitHandler<CommonUpsertProductiveAffidavitDto> = (
    data: CommonUpsertProductiveAffidavitDto,
  ) => {
    mutate(data);
  };

  const defaultValues = {
    uuid: selectedAffidavit?.uuid,
    hectares: selectedAffidavit?.hectares
      ? Number(selectedAffidavit?.hectares)
      : undefined,
    exploitationType: selectedAffidavit?.exploitationType,
    startDate: selectedAffidavit?.startDate
      ? Number(selectedAffidavit?.startDate)
      : undefined,
    endDate: selectedAffidavit?.endDate
      ? Number(selectedAffidavit?.endDate)
      : undefined,
    renspa: selectedAffidavit?.renspa,
    renspaIsCurrent: selectedAffidavit?.renspaIsCurrent || undefined,
    facilityUuid,
  };

  const formMethods = useForm<CommonUpsertProductiveAffidavitDto>({
    resolver,
    defaultValues,
  });
  const { formState, trigger, watch } = formMethods;

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  useEffect(() => {
    if (formState.isSubmitted) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  return { formMethods, onSubmit, isPendingUpsertAffidavit };
};
